import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BrainCircuit,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

import digavouchLogo from "@/assets/digavouch-logo.svg";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const DEMO_ROUTES = [
  // The two IDW recruiting lanes (ADR-014). One page, `/demo/applicant-flow`;
  // the `?flow=` param hands the demo a synthetic workflow definition so it
  // renders the definition-driven plan instead of the leasing one. See
  // @/lib/demoFlows.
  //
  // ⚠️ The LEASING journey is deliberately not carded here, and the route is
  // deliberately still live. Bare `/demo/applicant-flow` renders LEGACY_PLAN,
  // and `demoFlowFromParam` resolves an unrecognised `?flow=` to null — so a
  // stale or mistyped demo link lands on a complete, walkable flow rather than
  // a blank screen. Hiding the card is presentational; deleting the route
  // would remove that floor.
  {
    href: "/demo/applicant-flow?flow=high_volume",
    title: "Applicant: high volume",
    description: "Five screens, no ID check, digital account optional.",
    icon: ShieldCheck,
  },
  {
    href: "/demo/applicant-flow?flow=tech_role",
    title: "Applicant: tech role",
    description: "Six screens, ID check second, digital account required.",
    icon: ShieldCheck,
  },
  // The voucher flow takes the SAME `?flow=` parameter, so the referee surface
  // can be shown in the vertical the walkthrough is in. Without it this page
  // renders leasing copy to a recruiting referee — see #177, which also covers
  // why the liability disclaimers stay leasing regardless.
  {
    href: "/demo/voucher-flow?flow=tech_role",
    title: "Voucher",
    description: "Show the supporting referee experience and the response steps.",
    icon: Users,
  },
  {
    href: "/demo/applicant/juan-alvarez",
    title: "TA team view",
    description: "Open the sample review page with the full evidence summary.",
    icon: BrainCircuit,
  },
  // ⚠️ `/demo-new/applicant-flow` is deliberately not carded, and its route is
  // deliberately still live — same treatment as the leasing journey above.
  // It is a React port of a bespoke HTML prototype with its own 2,300-line
  // stylesheet, no backend, and no vertical: it renders a fixed five-step
  // leasing walkthrough whatever lane the viewer is on. Hiding the card is
  // presentational; the page is still reachable by URL for design review.
];

function RouteCard({
  href,
  title,
  description,
  icon: Icon,
}: {
  href: string;
  title: string;
  description: string;
  icon: typeof Sparkles;
}) {
  return (
    <Card className="border-slate-200/80 bg-white/85 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_28px_70px_rgba(15,23,42,0.12)]">
      <CardContent className="p-5 sm:p-6 flex h-full flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div className="rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 p-3 text-white shadow-lg shadow-violet-500/20">
            <Icon className="h-5 w-5" />
          </div>
          <Badge
            variant="outline"
            className="border-violet-200 bg-violet-50 text-violet-700"
          >
            Demo
          </Badge>
        </div>

        <div className="space-y-2">
          <h2 className="text-lg font-semibold tracking-tight text-slate-950">{title}</h2>
          <p className="text-sm leading-6 text-slate-600">{description}</p>
        </div>

        <div className="mt-auto pt-2">
          <Button asChild className="w-full sm:w-auto bg-slate-950 text-white hover:bg-slate-800">
            <Link to={href}>
              Open route
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function DemoHome() {
  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-slate-200/80 bg-[radial-gradient(circle_at_top_left,_rgba(124,58,237,0.16),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(14,165,233,0.14),_transparent_28%),linear-gradient(180deg,_rgba(255,255,255,0.92),_rgba(248,250,252,0.98))] px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-72 w-72 -translate-x-1/2 rounded-full bg-violet-500/10 blur-3xl" />
        <div className="absolute right-0 top-24 h-64 w-64 rounded-full bg-sky-400/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-6xl space-y-8">
        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="grid gap-6 lg:grid-cols-[1.25fr_0.75fr] lg:items-end"
        >
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-violet-700">
              <Sparkles className="h-3.5 w-3.5" />
              Demo hub
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src={digavouchLogo} alt="DigaVouch" className="h-10 w-10 shrink-0 rounded-xl" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                    DigaVouch
                  </p>
                  <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                    Demo routes, all in one place.
                  </h1>
                </div>
              </div>

              <p className="max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                This page is a clean entry point for walking through the current demo
                surfaces without jumping between tabs. Use it to open the applicant,
                voucher, or operator review experiences from a single route.
              </p>
            </div>

          </div>

          <Card className="border-slate-200/70 bg-white/90 shadow-[0_20px_50px_rgba(15,23,42,0.08)] backdrop-blur-sm">
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Current shell
                </div>
                <div className="mt-1 text-xl font-semibold tracking-tight text-slate-950">
                  Public app canvas
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Available flows
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-950">
                    {DEMO_ROUTES.length}
                  </div>
                  <p className="mt-1 text-sm text-slate-600">Linked demo routes ready to open.</p>
                </div>

                <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4">
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-700">
                    Style
                  </div>
                  <div className="mt-2 text-base font-semibold text-slate-950">
                    Intentional, card-first, product-like
                  </div>
                  <p className="mt-1 text-sm text-slate-600">Matches the app shell and brand colors.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.header>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {DEMO_ROUTES.map((route) => (
            <RouteCard key={route.href} {...route} />
          ))}
        </section>
      </div>
    </div>
  );
}
